import {
  addPopinToLocalSorage,
  disabledURLs,
  isPopinCooldownOver,
  isTimeToDisplayFromMostRecentPopinUp,
} from '../business/Popin.util';
import { MissingUserInformation } from './MissingUserInformationPopin.component';
import { NewsletterPopin } from './NewsletterPopin.component';
import { SignInSignUpPopin } from './SignInSignUpPopin.component';
import { fetchPopins } from 'features/popin/business/Popin.api';
import router, { Router } from 'next/router';
import { ActivityCode, Address, PopinEnum } from 'pleinchamp-api-client';
import { ComponentPropsWithRef, FC, useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { usePlcApiCall } from '@api/business/api.hook';
import { Backdrop } from '@components/Backdrop/Backdrop.component';
import { CloseIcon } from '@components/icons/Close.icon';
import { Logo } from '@components/Logo/Logo.component';
import { Dialog, DialogContent, DialogTitle, IconButton, withStyles } from '@material-ui/core';
import { RootState } from '@store/store';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { Spacing } from '@styles/Spacing.style';
import classNames from 'classnames';

const StyledDialog = withStyles(theme => ({
  paper: {
    padding: `${Spacing.s}px ${Spacing.m}px ${Spacing.m}px`,
    [theme.breakpoints.up('sm')]: {
      maxWidth: '800px',
      padding: `${Spacing.m}px ${Spacing.l}px min(${Spacing.xl}px, 5vh)`,
    },
  },
}))(Dialog);

const StyledDialogTitle = withStyles({
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    marginBottom: Spacing.basis,
    padding: 0,
  },
})(DialogTitle);
const StyledDialogContent = withStyles({ root: { display: 'flex', flexDirection: 'column', padding: 0 } })(
  DialogContent
);

const buildPopinContent = (
  name: PopinEnum,
  close: () => void,
  isAuthenticated?: boolean,
  newsletterEnabled?: boolean,
  activities?: ActivityCode[],
  address?: Address
) => {
  if (name.valueOf() === PopinEnum.NEWSLETTER.valueOf()) {
    if (!isAuthenticated || (isAuthenticated && !newsletterEnabled)) {
      return (
        <FocusLock autoFocus={true}>
          <NewsletterPopin close={close} />
        </FocusLock>
      );
    }
  } else if (name.valueOf() === PopinEnum.CONNECTION_REGISTRATION.valueOf()) {
    if (!isAuthenticated) {
      return <SignInSignUpPopin close={close} />;
    }
  } else if (name.valueOf() === PopinEnum.MISSING_INFO.valueOf()) {
    if (isAuthenticated && (!address?.zipPostcode || activities === undefined || activities.length === 0)) {
      return (
        <FocusLock autoFocus={true}>
          <MissingUserInformation activities={activities} close={close} zipPostcode={address?.zipPostcode} />
        </FocusLock>
      );
    }
  }
  return null;
};

const Popin: FC<ComponentPropsWithRef<'div'>> = ({ className }) => {
  const rootClassName = classNames('plc-modal', className, {
    'force-full-screen': false,
  });
  const { t } = useTranslation(['common']);
  const isMobile = useIsBreakpointDown(Breakpoint.s);
  const [open, setOpen] = useState(false);
  const { data: popins } = usePlcApiCall(fetchPopins, []);
  const [popInComponent, setPopInComponent] = useState<JSX.Element>();
  let timer: NodeJS.Timeout;
  const timeout = process.env.NEXT_PUBLIC_TIMEOUT_POPIN ? parseInt(process.env.NEXT_PUBLIC_TIMEOUT_POPIN, 10) : 30000;

  const rootStateRef = useRef<RootState>();
  const {
    settings: { isPreferencesLoaded },
    loading: {
      effects: {
        user: { fetchUser: isLoading },
      },
    },
    auth: { isAuthenticated },
  } = useSelector((state: RootState) => {
    rootStateRef.current = state;
    return state;
  });

  function handleClose() {
    setOpen(false);
  }

  const closeButtonInnerHtml = (
    <IconButton onClick={handleClose} title={t('common.close')}>
      <CloseIcon width={24} />
    </IconButton>
  );
  const closeButton = (
    <FocusLock className="close-button-wrapper" group="modalActiveSlide">
      {closeButtonInnerHtml}
    </FocusLock>
  );
  useEffect(() => {
    const onRouteChangePopin = () => {
      if (isLoading) return;
      if (isAuthenticated && !isPreferencesLoaded) return;
      if (popins === undefined) return;
      const availablePopins = popins.filter(p => isPopinCooldownOver(p.name, p.delay));
      if (!availablePopins || availablePopins.length === 0) return;

      timer = setTimeout(() => {
        if (!isTimeToDisplayFromMostRecentPopinUp()) return;
        if (disabledURLs.some((url: string) => router.asPath.includes(url))) return;
        if (rootStateRef.current?.layout.authModalView !== undefined) return;

        availablePopins.every(item => {
          const component = buildPopinContent(
            item.name,
            handleClose,
            rootStateRef.current?.auth.isAuthenticated,
            rootStateRef.current?.settings.preferences.newsletterEnabled,
            rootStateRef.current?.settings.preferences.activities,
            rootStateRef.current?.user.user?.address
          );
          if (component) {
            setPopInComponent(component);
            addPopinToLocalSorage(item.name);
            setOpen(true);
            return false;
          }
          return true;
        });

        clearTimeout(timer);
      }, timeout);
    };

    onRouteChangePopin();
    Router.events.on('routeChangeComplete', onRouteChangePopin);
    return () => {
      Router.events.off('routeChangeComplete', onRouteChangePopin);
    };
  }, [popins, isLoading, isPreferencesLoaded]);

  if (!popInComponent) return null;

  return (
    <StyledDialog
      BackdropComponent={Backdrop}
      className={rootClassName}
      disableEnforceFocus={true}
      fullScreen={isMobile}
      open={open}
      scroll="paper"
    >
      <StyledDialogTitle className="plc-modal" disableTypography={true}>
        {closeButton}
        <Logo height="50px" isPro={false} />
      </StyledDialogTitle>
      <StyledDialogContent>{popInComponent}</StyledDialogContent>
    </StyledDialog>
  );
};

export { Popin };
